import React, { useEffect, useRef, useState } from 'react';
import cc from 'classcat';
import StatusBadge from '../../StatusBadge';
import { InvoiceStatus } from '@cyberlabrs/helpers';
import { PdfViewer } from '../PdfViewer';
import { MixerLoader } from '../MixerLoader';
import { useTranslation } from '../../../hooks/useTranslation';
import { Button } from '../Button';
import { useNavigate } from 'react-router';
import { InputSearchFormikDebounce } from '../InputSearchDebounce';
import { Formik } from 'formik';
import { IMenuItem } from '../Select';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { FormValues } from '../../InvoiceTemplate';
import { useResizeListener } from '../../../hooks/windowEvents/useResizeListener';
import { Modal } from '../Modal';
import { YesNo } from '../YesNo';
import { useHttpPatchInvoiceStatus } from '../../../hooks';

export interface SidebarCardData {
    id: string;
    number: string;
    clientName: string;
    status: InvoiceStatus;
    total?: number;
    s3name: string;
    fileUrl: string;
}

interface IContainerWithSidebarCards {
    elements: SidebarCardData[];
    isLoading: boolean;
    loadMore?: () => void;
    hasMore: boolean;
    setSelectedInvoice: React.Dispatch<React.SetStateAction<SidebarCardData>>;
    selectedInvoice?: SidebarCardData;
    setQueries: React.Dispatch<React.SetStateAction<FormValues>>;
}

export interface ITitleButton {
    iconSrc: string;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
}

type PossibleNewStatus = InvoiceStatus.SENT | InvoiceStatus.PAID;

export const ContainerWithSidebarCards = ({
    elements,
    isLoading,
    loadMore,
    setSelectedInvoice,
    selectedInvoice,
    setQueries,
}: IContainerWithSidebarCards) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const filtersRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLImageElement>(null);

    useClickOutside(filtersRef, () => setAreFiltersExpanded(false), toggleRef);

    const [areFiltersExpanded, setAreFiltersExpanded] = useState<boolean>(false);
    const [isChangeStatusModalVisible, setIsChangeStatusModalVisible] = useState(false);
    const [newStatus, setNewStatus] = useState<PossibleNewStatus>(InvoiceStatus.SENT);
    const [tempFilters, setTempFilters] = useState<string[]>([]);
    const isMobile = useResizeListener().isMaxWidthLarge;
    const [invoices, setInvoices] = useState(elements);
    const { changeStatus } = useHttpPatchInvoiceStatus();

    useEffect(() => {
        setInvoices(elements);
    }, [elements]);

    const { t } = useTranslation('invoice');
    const menuItems: IMenuItem[] = [
        { label: t('draft'), value: 'DRAFT' },
        { label: t('ready'), value: 'READY' },
        { label: t('sent'), value: 'SENT' },
        { label: t('overdue'), value: 'OVERDUE' },
        { label: t('paid'), value: 'PAID' },
        { label: t('sendingFailed'), value: 'SENDING_FAILED' },
    ];

    const navigate = useNavigate();

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
            loadMore?.();
        }
    };

    const handleClick = (selected: SidebarCardData) => {
        setSelectedInvoice(selected);
    };

    const handleCheckboxChange = (value: string) => {
        setTempFilters(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
    };

    const handleExportSingle = async (invoice: SidebarCardData) => {
        try {
            const response = await fetch(invoice.fileUrl, { mode: 'cors' });
            if (!response.ok) throw new Error('Network response was not ok');

            const arrayBuffer = await response.arrayBuffer();
            const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice-${invoice.number}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(`Error downloading invoice ${invoice.number}:`, err);
        }
    };

    const handleStatusChange = async (status: PossibleNewStatus, invoiceId?: string) => {
        if (!invoiceId) return;
        await changeStatus(invoiceId, status);
        setSelectedInvoice(prev => ({ ...prev, status }));
        setInvoices(prev => prev.map(inv => (inv.id === invoiceId ? { ...inv, status } : inv)));
        setIsChangeStatusModalVisible(false);
    };

    return (
        <Formik<FormValues> initialValues={{ search: '', statuses: [] }} onSubmit={() => {}}>
            {formik => (
                <div className="container-with-sidebar-cards">
                    <div className="container-with-sidebar-cards__main">
                        <div className="container-with-sidebar-cards__sidebar" ref={containerRef} onScroll={handleScroll}>
                            <div className="container-with-sidebar-cards__sidebar__title-container">
                                <h3 className="container-with-sidebar-cards__sidebar__title">{isMobile ? t('mobilePreview') : t('preview')}</h3>
                                <p className="container-with-sidebar-cards__sidebar__title-subtext">{t('searchText')}</p>
                            </div>
                            <div className="container-with-sidebar-cards__search-container">
                                <InputSearchFormikDebounce
                                    id="search"
                                    placeholder={t('search')}
                                    context={formik}
                                    className="container-with-sidebar-cards__search"
                                    onChange={e => {
                                        const value = e.target.value;
                                        formik.setFieldValue('search', value);
                                        setQueries(prev => ({ ...prev, search: value }));
                                    }}
                                    isCloseButtonVisible={formik.values.search !== ''}
                                    closeButtonOnClick={() => {
                                        formik.setFieldValue('search', '');
                                        setQueries(prev => ({ ...prev, search: '' }));
                                    }}
                                />
                                {!isMobile && (
                                    <div className="invoices__actions__icons__filters-container">
                                        <img
                                            className="invoices__actions__icons__filters-container__icon"
                                            ref={toggleRef}
                                            src={areFiltersExpanded ? '/svg/common/filters-blue.svg' : '/svg/common/filters.svg'}
                                            alt="Filter"
                                            onClick={() => {
                                                setAreFiltersExpanded(prev => !prev);
                                            }}
                                        />
                                        {areFiltersExpanded && (
                                            <div ref={filtersRef} className="invoices__actions__filters">
                                                <p className="invoices__actions__filters__title">{t('status')}</p>
                                                <div className="invoices__actions__filters__options">
                                                    {menuItems.map(item => (
                                                        <label key={item.value} className="invoices__actions__filters__checkbox">
                                                            <input
                                                                type="checkbox"
                                                                checked={tempFilters.includes(item.value as string)}
                                                                onChange={() => handleCheckboxChange(item.value as string)}
                                                            />
                                                            <span>{item.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                <div className="invoices__actions__filters__options__buttons">
                                                    <Button
                                                        text={t('applyFilters')}
                                                        onClick={() => {
                                                            formik.setFieldValue('statuses', tempFilters);
                                                            setQueries(prev => ({
                                                                ...prev,
                                                                statuses: tempFilters as InvoiceStatus[],
                                                            }));
                                                            setAreFiltersExpanded(false);
                                                        }}
                                                    />
                                                    <Button
                                                        text={t('resetFilters')}
                                                        variant="text"
                                                        color="blue"
                                                        onClick={() => {
                                                            setTempFilters([]);
                                                            formik.setFieldValue('statuses', []);
                                                            setQueries(prev => ({ ...prev, statuses: [] }));
                                                            setAreFiltersExpanded(false);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {invoices.map((element, index) => (
                                <a
                                    href={`#${element.id}`}
                                    key={element.id + element.number + index}
                                    onClick={e => {
                                        e.preventDefault();
                                        if (isMobile) {
                                            navigate(`/invoices/preview`, { state: { invoice: element } });
                                        } else {
                                            handleClick(element);
                                        }
                                    }}
                                >
                                    <div
                                        className={cc([
                                            'container-with-sidebar-cards__card',
                                            { 'container-with-sidebar-cards__card--selected': element?.id === selectedInvoice?.id },
                                        ])}
                                    >
                                        <div className="container-with-sidebar-cards__card-title-container">
                                            <div className="container-with-sidebar-cards__card-title">{element.number}</div>
                                            <div className="container-with-sidebar-cards__card-subtitle">{element.clientName}</div>
                                        </div>
                                        {element.status && <StatusBadge status={element.status} />}
                                    </div>
                                </a>
                            ))}
                            {isLoading && <MixerLoader position="centerOnParent" size="small" />}
                        </div>
                        <div className="container-with-sidebar-cards__content-wrapper">
                            {!isLoading && !invoices.length ? (
                                <div className="container-with-sidebar-cards__no-content">
                                    <h3>{t('noInvoices')}</h3>
                                </div>
                            ) : (
                                <div className="container-with-sidebar-cards__content">
                                    {selectedInvoice?.id && (
                                        <div className="container-with-sidebar-cards__content-header">
                                            <div className="container-with-sidebar-cards__content-header__title-container">
                                                <h1 className="heading-2">{selectedInvoice?.number}</h1>
                                                <h4>{selectedInvoice?.clientName}</h4>
                                            </div>
                                            <div className="container-with-sidebar-cards__content-header__buttons">
                                                <img
                                                    className="container-with-sidebar-cards__content-header__download"
                                                    src="/svg/common/download-blue.svg"
                                                    alt="Download"
                                                    onClick={() => handleExportSingle(selectedInvoice)}
                                                />

                                                {(selectedInvoice?.status === InvoiceStatus.DRAFT ||
                                                    selectedInvoice?.status === InvoiceStatus.READY ||
                                                    selectedInvoice?.status === InvoiceStatus.SENT) && (
                                                    <Button
                                                        text={selectedInvoice?.status === InvoiceStatus.SENT ? t('markAsPaid') : t('markAsSent')}
                                                        variant="bordered"
                                                        className="container-with-sidebar-cards__content-header-button"
                                                        onClick={() => {
                                                            setIsChangeStatusModalVisible(true);
                                                            setNewStatus(
                                                                selectedInvoice?.status === InvoiceStatus.SENT
                                                                    ? InvoiceStatus.PAID
                                                                    : InvoiceStatus.SENT
                                                            );
                                                        }}
                                                    />
                                                )}
                                                {selectedInvoice?.status !== InvoiceStatus.SENDING_FAILED &&
                                                    selectedInvoice?.status !== InvoiceStatus.OVERDUE && (
                                                        <Button
                                                            text={t('editInvoice')}
                                                            variant="contained"
                                                            className="container-with-sidebar-cards__content-header-button"
                                                            onClick={() => navigate(`/invoices/${selectedInvoice?.id}/edit`)}
                                                        />
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="container-with-sidebar-cards__content-invoice">
                                        <PdfViewer url={selectedInvoice?.fileUrl ? selectedInvoice?.fileUrl : ''} scale={1.5} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <Modal isVisible={isChangeStatusModalVisible} closeModal={() => setIsChangeStatusModalVisible(false)}>
                        <YesNo
                            className="submit-timesheet__yes-no-modal"
                            description={newStatus === InvoiceStatus.PAID ? t('changeStatusToPaidDescription') : t('changeStatusToSentDescription')}
                            title={t('changeStatusTitle')}
                            onYes={() => handleStatusChange(newStatus, selectedInvoice?.id)}
                            onNo={() => setIsChangeStatusModalVisible(false)}
                        />
                    </Modal>
                </div>
            )}
        </Formik>
    );
};
